import type { Prospect, ProspectStatus } from '../types/prospect'
import { PROSPECT_STATUSES } from '../types/prospect'

// Constants
export const ARCHIVED_STATUSES: ProspectStatus[] = ['withdrawn', 'converted']

const STAGE_PRIORITY: Record<ProspectStatus, number> = {
  'approved': 50,
  'application': 40,
  'viewing': 20,
  'lead': 10,
  'rejected': 0,
  'withdrawn': 0,
  'converted': 0,
}

// Helper to check if prospect is already linked to a tenancy
export function isLinkedToTenancy(prospect: Prospect): boolean {
  return Boolean(
    prospect.status === 'converted' ||
    prospect.converted_tenancy_id ||
    (prospect.notes && prospect.notes.includes('Tenancy ID:'))
  )
}

// Status badge configuration
export function getStatusBadgeConfig(status: ProspectStatus): { label: string; bg: string; text: string } {
  const statusConfig = PROSPECT_STATUSES.find(s => s.value === status)
  const colorMap: Record<string, { bg: string; text: string }> = {
    zinc: { bg: 'var(--color-muted)', text: 'var(--text-muted)' },
    blue: { bg: 'var(--color-info-muted)', text: 'var(--color-info)' },
    purple: { bg: 'rgba(168, 85, 247, 0.15)', text: 'rgb(168, 85, 247)' },
    amber: { bg: 'var(--color-warning-muted)', text: 'var(--color-warning)' },
    emerald: { bg: 'var(--color-success-muted)', text: 'var(--color-success)' },
    red: { bg: 'var(--color-danger-muted)', text: 'var(--color-danger)' }
  }
  const colors = colorMap[statusConfig?.color || 'zinc']
  return { label: statusConfig?.label || status, ...colors }
}

// Check if a prospect needs action from the landlord
export function prospectNeedsAction(prospect: Prospect): boolean {
  const viewings = prospect.viewings || []
  const activeApplications = (prospect.applications || []).filter(a => !['rejected', 'withdrawn'].includes(a.status))
  const propertyInterests = prospect.property_interests || []

  const now = new Date()
  const hasInterestedViewing = viewings.some(v => v.outcome === 'interested')
  const hasDeclinedWithProposal = viewings.some(v =>
    v.status === 'declined' && v.declined_reason?.includes('Proposed alternative times')
  )
  const hasViewingRequest = propertyInterests.some(pi => pi.interest_level === 'viewing_requested')
  const hasApplication = activeApplications.length > 0
  const currentApp = activeApplications[0]
  const referencingRecord = Array.isArray(currentApp?.referencing)
    ? currentApp.referencing[0]
    : currentApp?.referencing
  const hasReferencing = !!referencingRecord
  const referencingPassed = hasApplication && currentApp.status !== 'approved' && hasReferencing && referencingRecord.status === 'passed'

  const upcomingViewings = viewings.filter(v =>
    ['scheduled', 'confirmed'].includes(v.status) && new Date(v.scheduled_at) > now
  )
  const pastViewingsNeedingOutcome = viewings.some(v =>
    (v.status === 'completed' || (['scheduled', 'confirmed'].includes(v.status) && new Date(v.scheduled_at) < now))
    && (!v.outcome || v.outcome === 'pending')
  )

  const needsViewing = ['lead', 'viewing'].includes(prospect.status) && propertyInterests.length > 0 && upcomingViewings.length === 0 && !hasInterestedViewing
  const needsPropertyInterest = ['lead', 'viewing'].includes(prospect.status) && propertyInterests.length === 0
  const needsApplication = hasInterestedViewing && !hasApplication
  // Note: 'submitted' applications are tracked separately in "Applications to Review"
  const needsTenancy = prospect.status === 'approved' && !isLinkedToTenancy(prospect)

  // Check if documents are missing (only when referencing is actively in progress)
  const requiredDocs = (referencingRecord?.required_documents || ['id_passport', 'proof_of_address', 'payslip', 'bank_statement', 'right_to_rent']) as string[]
  const uploadedDocTypes = new Set((referencingRecord?.documents || []).map((d: { document_type: string }) => d.document_type))
  const referencingInProgress = hasReferencing && referencingRecord.status === 'in_progress'
  const hasMissingDocs = referencingInProgress && requiredDocs.some(doc => !uploadedDocTypes.has(doc))

  // All docs uploaded — landlord needs to act (start or complete referencing)
  const allDocsUploaded = requiredDocs.every(doc => uploadedDocTypes.has(doc))
  const docsReadyToReference = hasReferencing && referencingRecord.status === 'pending' && allDocsUploaded
  const allDocsUploadedNeedsChecks = referencingInProgress && allDocsUploaded

  return hasDeclinedWithProposal || hasViewingRequest || pastViewingsNeedingOutcome || needsViewing || needsPropertyInterest || needsApplication || referencingPassed || needsTenancy || hasMissingDocs || docsReadyToReference || allDocsUploadedNeedsChecks
}

// Calculate prospect priority for sorting
export function getProspectPriority(prospect: Prospect): number {
  const viewings = prospect.viewings || []
  const activeApplications = (prospect.applications || []).filter(a => !['rejected', 'withdrawn'].includes(a.status))
  const now = new Date()
  const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000)

  const hasInterestedViewing = viewings.some(v => v.outcome === 'interested')
  const hasDeclinedWithProposal = viewings.some(v =>
    v.status === 'declined' && v.declined_reason?.includes('Proposed alternative times')
  )
  const hasApplication = activeApplications.length > 0
  const currentApp = activeApplications[0]
  const referencingRecord = Array.isArray(currentApp?.referencing)
    ? currentApp.referencing[0]
    : currentApp?.referencing
  const hasReferencing = !!referencingRecord
  const referencingPassed = hasApplication && currentApp.status !== 'approved' && hasReferencing && referencingRecord.status === 'passed'

  const needsApplication = hasInterestedViewing && !hasApplication
  // Show "Start referencing" when application is submitted but referencing not yet started
  // (referencing may exist with 'pending' status for early document collection)
  const needsReferencing = hasApplication && currentApp.status === 'submitted'
  const needsTenancy = prospect.status === 'approved' && !isLinkedToTenancy(prospect)
  const isNewLead = new Date(prospect.created_at) > yesterday && prospect.status === 'lead'

  // All docs uploaded but referencing not yet started
  const requiredDocs = (referencingRecord?.required_documents || ['id_passport', 'proof_of_address', 'payslip', 'bank_statement', 'right_to_rent']) as string[]
  const uploadedDocTypes = new Set((referencingRecord?.documents || []).map((d: { document_type: string }) => d.document_type))
  const allDocsUploaded = requiredDocs.every(doc => uploadedDocTypes.has(doc))
  const docsReadyToReference = hasReferencing && referencingRecord.status === 'pending' && allDocsUploaded
  const referencingInProgress = hasReferencing && referencingRecord.status === 'in_progress'
  const allDocsUploadedNeedsChecks = referencingInProgress && allDocsUploaded

  // Base priority from pipeline stage
  let priority = STAGE_PRIORITY[prospect.status] || 0

  // Add bonus for action-needed items
  if (hasDeclinedWithProposal || needsApplication || needsReferencing || referencingPassed || needsTenancy || docsReadyToReference || allDocsUploadedNeedsChecks) {
    priority += 5
  }

  // Small bonus for new leads
  if (isNewLead) {
    priority += 2
  }

  return priority
}

// Check if prospect has a viewing scheduled within the next 48 hours
export function hasViewingSoon(prospect: Prospect): boolean {
  const now = new Date()
  const in48h = new Date(now.getTime() + 48 * 60 * 60 * 1000)
  return (prospect.viewings || []).some(v =>
    ['scheduled', 'confirmed'].includes(v.status) &&
    new Date(v.scheduled_at) > now &&
    new Date(v.scheduled_at) <= in48h
  )
}

// Check if prospect has referencing with missing required documents
export function hasAwaitingDocs(prospect: Prospect): boolean {
  const activeApps = (prospect.applications || []).filter(a => !['rejected', 'withdrawn'].includes(a.status))
  const app = activeApps[0]
  const ref = Array.isArray(app?.referencing) ? app.referencing[0] : app?.referencing
  if (!ref || !['pending', 'in_progress'].includes(ref.status)) return false
  const required = (ref.required_documents || ['id_passport', 'proof_of_address', 'payslip', 'bank_statement', 'right_to_rent']) as string[]
  const uploaded = new Set((ref.documents || []).map((d: { document_type: string }) => d.document_type))
  return required.some(doc => !uploaded.has(doc))
}

export function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  })
}

export function formatRelativeTime(dateStr: string): string {
  const now = new Date()
  const date = new Date(dateStr)
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / (1000 * 60))
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

  if (diffMins < 1) return 'Just now'
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays === 1) return 'Yesterday'
  if (diffDays < 7) return `${diffDays}d ago`
  return formatDate(dateStr)
}

